import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, CircularProgress, Chip, Grid
} from '@mui/material';
import { CalendarToday as CalendarIcon, AccessTime as TimeIcon } from '@mui/icons-material';
import Navbar from '../navbar';
import { getDoctorAvailability } from '../../service/slots';

const MyAvailability = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const doctorId = user.id;

  const today = new Date();
  const defaultEnd = new Date(today);
  defaultEnd.setDate(defaultEnd.getDate() + 7);

  const [startDate, setStartDate] = useState(today.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(defaultEnd.toISOString().split('T')[0]);
  const [slotsByDate, setSlotsByDate] = useState({});
  const [summary, setSummary] = useState({ total: 0, available: 0, booked: 0 });
  const [loading, setLoading] = useState(false);

  const fetchSlots = async () => {
    if (!doctorId) return;
    try {
      setLoading(true);
      const data = await getDoctorAvailability(doctorId, startDate, endDate);
      setSlotsByDate(data.slots_by_date || {});
      setSummary({
        total: data.total_slots || 0,
        available: data.available_slots || 0,
        booked: data.booked_slots || 0,
      });
    } catch (err) {
      console.error('Error fetching slots:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlots();
  }, []);

  const sortedDates = Object.keys(slotsByDate).sort();

  const formatDate = (dateStr) => {
    // Handle various date formats from API (e.g. "2026-08-03T00:00:00Z", "2026-08-03", etc.)
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatTime = (t) => {
    if (!t) return '';
    const parts = t.split(':');
    const h = parseInt(parts[0], 10);
    const m = parts[1];
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${m} ${ampm}`;
  };

  const isExpired = (dateStr, endTime) => {
    const now = new Date();
    // Extract just the date part
    const datePart = dateStr.split('T')[0];
    const slotEnd = new Date(`${datePart}T${endTime}`);
    return slotEnd < now;
  };

  return (
    <Box sx={{ background: '#f5f7fa', minHeight: '100vh' }}>
      <Navbar />
      <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, px: 2, pb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>My Availability</Typography>

        {/* Date Filter */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Typography variant="subtitle2" sx={{ color: '#6366f1', fontWeight: 600, mb: 2 }}>
            DATE RANGE
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ minWidth: 180 }}
            />
            <TextField
              label="End"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ minWidth: 180 }}
            />
            <Button
              variant="contained"
              onClick={fetchSlots}
              sx={{
                background: 'linear-gradient(135deg, #6366f1, #818cf8)',
                textTransform: 'none',
                borderRadius: 2,
                px: 3,
              }}
            >
              Search Slots
            </Button>
          </Box>
        </Paper>

        {/* Summary */}
        {!loading && summary.total > 0 && (
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Chip label={`Total: ${summary.total}`} sx={{ fontWeight: 600, fontSize: '0.85rem' }} />
            <Chip label={`Available: ${summary.available}`} color="success" sx={{ fontWeight: 600, fontSize: '0.85rem' }} />
            <Chip label={`Booked: ${summary.booked}`} color="primary" sx={{ fontWeight: 600, fontSize: '0.85rem' }} />
          </Box>
        )}

        {/* Loading */}
        {loading && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Slots by Date */}
        {!loading && sortedDates.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <Typography color="text.secondary">No slots found for the selected date range.</Typography>
          </Paper>
        )}

        {!loading && sortedDates.map((date) => {
          const slots = slotsByDate[date] || [];
          const sortedSlots = [...slots].sort((a, b) => a.start_time.localeCompare(b.start_time));

          return (
            <Paper key={date} sx={{ mb: 2, borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                px: 3, py: 1.5, borderBottom: '1px solid #e5e7eb'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon sx={{ fontSize: 20, color: '#6366f1' }} />
                  <Typography sx={{ fontWeight: 700 }}>{formatDate(date)}</Typography>
                </Box>
                <Chip label={`${slots.length} slots`} size="small" variant="outlined" />
              </Box>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={1.5}>
                  {sortedSlots.map((slot) => {
                    const expired = isExpired(slot.slot_date, slot.end_time);
                    const booked = slot.is_booked;
                    let borderColor = '#22c55e';
                    let bgColor = '#f0fdf4';
                    let statusText = 'Available';
                    let statusColor = '#22c55e';

                    if (booked) {
                      borderColor = '#3b82f6';
                      bgColor = '#eff6ff';
                      statusText = 'Booked';
                      statusColor = '#3b82f6';
                    } else if (expired) {
                      borderColor = '#d1d5db';
                      bgColor = '#fff';
                      statusText = 'Expired';
                      statusColor = '#9ca3af';
                    }

                    return (
                      <Grid item xs={6} sm={4} md={3} lg={2} key={slot.id}>
                        <Box sx={{
                          border: `1.5px solid ${borderColor}`,
                          borderRadius: 2,
                          p: 1.5,
                          background: bgColor,
                          minHeight: 70,
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <TimeIcon sx={{ fontSize: 15, color: '#64748b' }} />
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#334155' }}>
                              {formatTime(slot.start_time)} – {formatTime(slot.end_time)}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: '0.75rem', color: statusColor, fontWeight: 500 }}>
                            ● {statusText}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

export default MyAvailability;
